import { CommonCoursePreviewCard } from '@/components/ui'
import {
  BaseModal,
  CommonExamBoardSelectModalContent,
} from '@/components/ui/common/modal'
import { ExamBoards, ICourse } from '@/types/firebase'
import { CourseOverviewTabKey, IGenericError } from '@/types/shared'
import { notify } from '@/utils/index'
import { useRouter } from 'next/navigation'
import React, { FC, useCallback, useState } from 'react'

interface Props {
  course: ICourse
  templateRouteId?: string
  additionalCourseClickHandler?: (
    course: ICourse,
    examBoard: ExamBoards,
  ) => Promise<void>
}

export const CoursePreviewCard: FC<Props> = ({
  course,
  templateRouteId,
  additionalCourseClickHandler,
}) => {
  const [isSelectExamBoardModalOpened, setIsSelectExamBoardModalOpened] =
    useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const router = useRouter()

  const handleCourseClick = useCallback(
    async (course: ICourse, examBoard: ExamBoards) => {
      try {
        setIsLoading(true)

        const path = `/courses/${course.id}`

        const params = new URLSearchParams()
        params.append('tab', CourseOverviewTabKey.Overview)

        if (templateRouteId) {
          params.append('templateRouteId', templateRouteId)
        }

        if (examBoard) {
          params.append('examBoard', examBoard)
        }

        const queryString = params.toString()

        const href = `${path}?${queryString}`

        router.push(href)
      } catch (error: unknown) {
        console.error(
          'error handleCourseClick :>> ',
          (error as IGenericError)?.message || error,
        )

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [router, templateRouteId],
  )

  const courseClickHandler = additionalCourseClickHandler
    ? additionalCourseClickHandler
    : handleCourseClick

  return (
    <>
      <CommonCoursePreviewCard
        course={course}
        variant="minimal"
        handleCardClick={async () => {
          // if course contains only 1 exam board use it by default to prevent SelectExamBoardChange modal appear
          if (course.examBoards.length === 1) {
            await courseClickHandler(course, course.examBoards[0])

            return
          }

          setIsSelectExamBoardModalOpened(true)
        }}
        isLoading={isLoading}
        contentClassName={
          'relative z-[0] w-full h-full min-h-[220px] flex justify-between flex-col gap-5 rounded-xl bg-white transition duration-400 overflow-hidden'
        }
        wrapperClassName={`block w-full rounded-xl overflow-hidden bg-white border border-v8-line shadow-sm hover:shadow-md transition-all duration-200`}
      />

      <BaseModal
        id={`select-exam-board-modal-${course.id}`}
        key={course.id}
        isOpen={isSelectExamBoardModalOpened}
        onClose={() => setIsSelectExamBoardModalOpened(false)}
        content={
          <CommonExamBoardSelectModalContent
            course={course}
            handleConfirmSelection={async (examBoard) => {
              await courseClickHandler(course, examBoard)

              setIsSelectExamBoardModalOpened(false)
            }}
            isLoading={isLoading}
          />
        }
        bodyAdditionalClassName="max-sm:w-full sm:min-w-[400px]"
        customZIndexClass={'z-[665]'}
        bodyBasicClassName="relative transition-all transform bg-white rounded-v8 border border-v8-line shadow-v8-card p-6"
      />
    </>
  )
}
